import React, { useState } from "react";
import { NeoButton, NeoInput, NeoCard, NeoDatePicker } from "../../../../../components/Common";
import { Formik, Form } from "formik";
import MemberSelector from "../editor/MemberSelector";
import WorkoutTabsEditor from "../editor/WorkoutTabsEditor";
import { toISODate } from "../../../../../utils/date";
import { SYSTEM_TEMPLATES } from "../../constants";

const normalizeSessions = (source, workoutType = "strength") => (
    (source?.sessions || source?.workouts || []).map(session => ({
        ...session,
        sessionType: session.sessionType || workoutType,
        blocks: (session.blocks || []).map(block => ({
            ...block,
            workoutType: block.workoutType || session.sessionType || workoutType,
            sets: block.sets || [],
        })),
    }))
);

/**
 * Formulário para prescrever um modelo de treino para aluno(s).
 * Componente de apresentação puro — recebe dados e callbacks via props.
 *
 * @param {Object}   template    - O modelo selecionado para prescrever
 * @param {Function} onPrescribe - Callback(prescriptionBase, membersList, saveAsTemplate)
 * @param {boolean}  isSaving
 * @param {Function} onCancel    - Voltar para edição de modelo
 * @param {Function} onDelete    - Callback(id, title)
 * @param {boolean}  isEditing   - Modo edição de prescrição existente
 * @param {Array}    userTemplates - Templates salvos do usuário
 * @param {Function} onAiGenerate - Abrir modal de IA
 */
const PrescriptionForm = ({
    template,
    onPrescribe,
    isSaving = false,
    onCancel,
    onDelete,
    isEditing = false,
    userTemplates = [],
    onAiGenerate,
}) => {
    const [selectedMembers, setSelectedMembers] = useState(template?.preselectedMembers || []);
    const [filterModality, setFilterModality] = useState("all");
    const [selectedTemplateId, setSelectedTemplateId] = useState("");
    const [startDate, setStartDate] = useState(template?.startDate ? toISODate(new Date(template.startDate)) : toISODate(new Date()));
    const [totalWeeks, setTotalWeeks] = useState(template?.totalWeeks || 4);
    const [saveAsTemplate, setSaveAsTemplate] = useState(false);

    // Sincroniza quando o template muda
    React.useEffect(() => {
        if (template?.preselectedMembers) {
            setSelectedMembers(template.preselectedMembers);
        }
    }, [template]);

    const endDate = (() => {
        if (!startDate || !totalWeeks) return "";
        const d = new Date(startDate);
        d.setDate(d.getDate() + (totalWeeks * 7));
        return toISODate(d);
    })();


    const handleAddMember = (member) => {
        if (selectedMembers.find(m => m.id === member.id)) return;
        setSelectedMembers(prev => [...prev, member]);
    };

    const handleRemoveMember = (memberId) => {
        setSelectedMembers(prev => prev.filter(m => m.id !== memberId));
    };

    const handleSubmit = (values) => {
        if (selectedMembers.length === 0) return;

        const isSingleDay = values.isSingleDay || false;

        const prescriptionBase = {
            idTemplate: values.id || null,
            templateTitle: values.title || "Treino",
            workoutType: values.workoutType || "strength",
            sessions: values.sessions || [],
            workouts: values.sessions || [],
            startDate,
            endDate: isSingleDay ? startDate : endDate,
            weeklyFrequency: values.weeklyFrequency || 1,
            isSingleDay,
            totalWeeks: isSingleDay ? 1 : totalWeeks,
        };

        onPrescribe(prescriptionBase, selectedMembers, saveAsTemplate);
    };

    if (!template) return null;

    return (
        <Formik
            initialValues={{
                ...template,
                title: template?.title || "Treino Direto",
                workoutType: template?.workoutType || "strength",
                sessions: normalizeSessions(template, template?.workoutType || "strength"),
                workouts: normalizeSessions(template, template?.workoutType || "strength"),
                weeklyFrequency: template?.weeklyFrequency || 3,
                isSingleDay: template?.isSingleDay || false,
            }}
            onSubmit={handleSubmit}
            enableReinitialize
        >
            {({ values, setFieldValue, handleChange, submitForm }) => {
                const isSingleDay = values.isSingleDay;

                const headerActions = (
                    <div className="d-flex gap-2 align-items-center">
                        {onCancel && (
                            <NeoButton
                                type="button"
                                variant="soft-secondary"
                                size="sm"
                                onClick={onCancel}
                                icon="mdi-arrow-left"
                            >
                                Voltar
                            </NeoButton>
                        )}
                        {onAiGenerate && !isEditing && (
                            <NeoButton
                                type="button"
                                variant="soft-info"
                                size="sm"
                                icon="mdi-robot-outline"
                                onClick={onAiGenerate}
                            >
                                Gerar com IA
                            </NeoButton>
                        )}
                        <NeoButton
                            type="button"
                            variant="primary"
                            size="sm"
                            loading={isSaving}
                            disabled={selectedMembers.length === 0}
                            onClick={submitForm}
                            icon="mdi-content-save-outline"
                        >
                            Salvar
                        </NeoButton>
                    </div>
                );

                return (
                    <Form>
                        <NeoCard
                            title={isEditing ? "Editar Prescrição" : (values.id ? "Prescrever Modelo" : "Novo Treino Direto")}
                            subtitle={isEditing ? "Altere a prescrição do treino" : "Defina os dados, alunos e a estrutura da planilha"}
                            headerIcon={isEditing ? "mdi-pencil-outline" : "mdi-account-plus-outline"}
                            headerActions={headerActions}
                            bodyClassName="p-4 bg-light-subtle pb-5"
                        >
                            {/* ── Configurações Principais (Card Único) ── */}
                            <NeoCard thinBorder className="mb-4" bodyClassName="p-4 bg-white">
                                <div className="row g-3 align-items-start">
                                    {(userTemplates?.length > 0 || SYSTEM_TEMPLATES?.length > 0) && (() => {
                                        const filteredSystem = SYSTEM_TEMPLATES.filter(t => filterModality === "all" || t.workoutType === filterModality);
                                        const filteredUser = userTemplates.filter(t => filterModality === "all" || t.workoutType === filterModality);

                                        return (
                                            <>
                                                <div className="col-md-4">
                                                    <NeoInput
                                                        label="Modalidade"
                                                        type="select"
                                                        value={filterModality}
                                                        onChange={e => setFilterModality(e.target.value)}
                                                    >
                                                        <option value="all">Todas</option>
                                                        <option value="swimming">Natação</option>
                                                        <option value="strength">Musculação</option>
                                                        <option value="running">Corrida</option>
                                                        <option value="wod">Cross / WOD</option>
                                                    </NeoInput>
                                                </div>
                                                <div className="col-md-8">
                                                    <NeoInput
                                                        label="Preencher com Modelo Pronto"
                                                        icon="mdi-auto-fix"
                                                        type="select"
                                                        value={selectedTemplateId}
                                                        onChange={(e) => {
                                                            const selectedId = e.target.value;
                                                            setSelectedTemplateId(selectedId);
                                                            if (!selectedId) return;
                                                            const allTemplates = [...(userTemplates || []), ...SYSTEM_TEMPLATES];
                                                            const selected = allTemplates.find(t => t.id === selectedId);
                                                            if (selected) {
                                                                const currentType = selected.workoutType || "strength";
                                                                const parsedSessions = normalizeSessions(selected, currentType);
                                                                setFieldValue("title", selected.title || "Treino");
                                                                setFieldValue("workoutType", currentType);
                                                                setFieldValue("sessions", parsedSessions);
                                                                setFieldValue("isSingleDay", selected.isSingleDay || false);
                                                                setFieldValue("weeklyFrequency", selected.weeklyFrequency || 1);
                                                                if (selected.totalWeeks) setTotalWeeks(selected.totalWeeks);
                                                            }
                                                        }}
                                                    >
                                                        <option value="">-- Escolha um modelo --</option>
                                                        {filteredSystem.length > 0 && (
                                                            <optgroup label="Modelos do Sistema">
                                                                {filteredSystem.map(t => (
                                                                    <option key={t.id} value={t.id}>{t.title}</option>
                                                                ))}
                                                            </optgroup>
                                                        )}
                                                        {filteredUser.length > 0 && (
                                                            <optgroup label="Seus Modelos Salvos">
                                                                {filteredUser.map(t => (
                                                                    <option key={t.id} value={t.id}>{t.title}</option>
                                                                ))}
                                                            </optgroup>
                                                        )}
                                                        {filteredSystem.length === 0 && filteredUser.length === 0 && (
                                                            <option value="" disabled>Nenhum modelo encontrado</option>
                                                        )}
                                                    </NeoInput>
                                                </div>
                                            </>
                                        );
                                    })()}

                                    {/* Título ou Cabeçalho do Modelo */}
                                    <div className="col-md-6">
                                        <NeoInput
                                            label="Título do Treino *"
                                            type="text"
                                            placeholder="Ex: Treino de Adaptação"
                                            value={values.title}
                                            onChange={e => setFieldValue("title", e.target.value)}
                                        />
                                    </div>

                                    {/* Formato da Prescrição */}
                                    <div className="col-md-6">
                                        <NeoInput
                                            label="Formato da Prescrição"
                                            type="select"
                                            value={isSingleDay ? "single" : "cycle"}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                if (val === "single") {
                                                    setFieldValue("isSingleDay", true);
                                                    setFieldValue("weeklyFrequency", 1);
                                                } else {
                                                    setFieldValue("isSingleDay", false);
                                                }
                                            }}
                                        >
                                            <option value="cycle">Ciclo / Ficha (Múltiplas Semanas)</option>
                                            <option value="single">Data Específica / Multisport</option>
                                        </NeoInput>
                                    </div>

                                    {/* Seleção de Alunos (Somente ao prescrever um Modelo novo) */}
                                    {values.id && !isEditing && (
                                        <div className="col-12 mb-2">
                                            <MemberSelector
                                                label="Adicionar Aluno"
                                                required
                                                onSelect={handleAddMember}
                                                onClear={null}
                                            />
                                            {selectedMembers.length > 0 && (
                                                <div className="mt-3">
                                                    <label className="neo-input-label mb-2">
                                                        Alunos selecionados ({selectedMembers.length})
                                                    </label>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {selectedMembers.map(member => (
                                                            <div key={member.id} className="d-flex align-items-center gap-2 px-3 py-1 bg-light rounded-pill border" style={{ borderColor: "rgba(72, 39, 18, 0.12)" }}>
                                                                <div className="rounded-circle bg-primary-subtle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 26, height: 26, overflow: "hidden" }}>
                                                                    {member.photo ? (
                                                                        <img src={member.photo} alt="" className="rounded-circle" style={{ width: 26, height: 26, objectFit: "cover" }} />
                                                                    ) : (
                                                                        <i className="mdi mdi-account text-primary font-size-13" />
                                                                    )}
                                                                </div>
                                                                <span className="font-size-13 fw-semibold mb-0" style={{ color: "var(--color-brand-primary)" }}>{member.name}</span>
                                                                <NeoButton
                                                                    type="button"
                                                                    variant="soft-danger"
                                                                    size="sm"
                                                                    circle
                                                                    icon="mdi-close"
                                                                    style={{ width: "20px", height: "20px", fontSize: "10px" }}
                                                                    onClick={() => handleRemoveMember(member.id)}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {!isSingleDay ? (
                                        <>
                                            <div className="col-md-3 col-6">
                                                <NeoInput
                                                    label="Dias por semana"
                                                    type="number"
                                                    min="1"
                                                    max="7"
                                                    value={values.weeklyFrequency || 3}
                                                    onChange={(e) => setFieldValue("weeklyFrequency", Number(e.target.value) || 3)}
                                                />
                                            </div>
                                            <div className="col-md-3 col-6">
                                                <NeoInput
                                                    label="Semanas (Duração)"
                                                    type="number"
                                                    min="1"
                                                    max="52"
                                                    value={totalWeeks}
                                                    onChange={(e) => setTotalWeeks(Number(e.target.value) || 4)}
                                                />
                                            </div>
                                            <div className="col-md-3 col-6">
                                                <NeoDatePicker
                                                    label="Início do Ciclo"
                                                    value={startDate}
                                                    onChange={setStartDate}
                                                />
                                            </div>
                                            <div className="col-md-3 col-6">
                                                <NeoDatePicker
                                                    label="Vencimento"
                                                    value={endDate}
                                                    onChange={() => {}}
                                                    disabled
                                                />
                                            </div>
                                        </>
                                    ) : (
                                        <div className="col-12">
                                            <NeoDatePicker
                                                label="Data do Treino"
                                                value={startDate}
                                                onChange={setStartDate}
                                            />
                                        </div>
                                    )}

                                    {/* Opção de Salvar como Modelo (Apenas para Treinos Diretos e Criação) */}
                                    {!values.id && !isEditing && (
                                        <div className="col-12 mt-2 pt-2 border-top">
                                            <div className="form-check form-switch mb-0">
                                                <NeoInput
                                                    type="checkbox"
                                                    id="saveAsTemplate"
                                                    className="form-check-input cursor-pointer"
                                                    checked={saveAsTemplate}
                                                    onChange={(e) => setSaveAsTemplate(e.target.checked)}
                                                />
                                                <label className="form-check-label cursor-pointer text-muted font-size-13 fw-medium" htmlFor="saveAsTemplate">
                                                    Salvar como modelo
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </NeoCard>

                            {/* ── Editor de Abas (Treinos) ── */}
                            <div className="mt-4">
                                <WorkoutTabsEditor
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    handleChange={handleChange}
                                />
                            </div>
                        </NeoCard>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default PrescriptionForm;
